import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import {
  useEmailSenderIdentity,
  useUpdateEmailSenderSlug,
  type EmailSenderIdentity,
} from "@/features/material-compliance/api/inquiryEmailApi"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useState } from "react"
import {
  NEEDS_WRITING_FIELD_CLASS,
  PlaceholderNotice,
} from "../../components/PlaceholderNotice"
import {
  LanguageSwitch,
  type ContentLanguage,
} from "../../components/SettingsControls"
import {
  SettingsField,
  SettingsPage,
  SettingsSection,
} from "../../components/SettingsPage"
import { SettingsWithPreview } from "../../components/SettingsPreview"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"
import { TemplateSaveButton } from "../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../components/TemplateSettingsLoader"
import { useUnsavedChanges } from "../../components/UnsavedChanges"
import { sameContent, useSavedDraft } from "../../hooks/useSavedDraft"
import { useTemplateSettingsSave } from "../../hooks/useTemplateSettingsSave"
import {
  EmailPreview,
  resolvePreviewVariables,
} from "../../previews/EmailPreview"
import { SenderPreview } from "../../previews/SenderPreview"
import { languagesNeedingWriting } from "../../setup/placeholders"
import {
  emailSignatureNeedsSetup,
  sendingAddressNeedsSetup,
} from "../../setup/setupChecks"

// The backend's rule (EmailService.is_valid_org_slug): 2–40 characters.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/

/** The part before the @. Once the first inquiry went out it is fixed:
 *  suppliers reply to it. */
function SendingAddressForm({ identity }: { identity: EmailSenderIdentity }) {
  const { translate } = useTranslation()
  const update = useUpdateEmailSenderSlug()
  const [slug, setSlug] = useSavedDraft(identity.slug ?? "")
  const [confirming, setConfirming] = useState(false)

  const trimmed = slug.trim()
  const valid = SLUG_PATTERN.test(trimmed)
  const dirty = trimmed !== (identity.slug ?? "")
  const invalid = dirty && trimmed.length > 0 && !valid
  const editable = !identity.isSet && update.canRun
  useUnsavedChanges(dirty && editable)

  async function onConfirm() {
    try {
      await update.mutateAsync(trimmed)
      setConfirming(false)
      toast.success(translate("SETTINGS/EMAIL_SENDING/ADDRESS_SAVED"))
    } catch {
      // useUpdateEmailSenderSlug already toasts the failure.
    }
  }

  return (
    <SettingsSection
      title={translate("SETTINGS/EMAIL_SENDING/ADDRESS_TITLE")}
      target="email.sendingAddress"
      // What the address is for and that it is set once, said once here.
      description={translate(
        identity.isSet
          ? "SETTINGS/EMAIL_SENDING/ADDRESS_DESCRIPTION_LOCKED"
          : "SETTINGS/EMAIL_SENDING/ADDRESS_DESCRIPTION",
      )}
      needsSetup={sendingAddressNeedsSetup(identity)}
      footer={
        editable && (
          <Button
            disabled={!dirty || !valid}
            onClick={() => setConfirming(true)}
          >
            {translate("MC_TPL_SAVE")}
          </Button>
        )
      }
    >
      <SettingsWithPreview
        preview={
          <SenderPreview
            address={trimmed ? `${trimmed}@${identity.domain}` : null}
          />
        }
      >
        <SettingsField
          htmlFor="settings-sender-slug"
          label={translate("MC_TPL_EMAIL_ADDRESS_LABEL")}
          // Only while the address breaks the rule; the field's border
          // marks it too.
          hint={invalid ? translate("MC_TPL_EMAIL_ADDRESS_INVALID") : undefined}
        >
          <InputGroup data-disabled={!editable}>
            <InputGroupInput
              id="settings-sender-slug"
              value={slug}
              disabled={!editable}
              aria-invalid={invalid}
              placeholder={translate("MC_TPL_EMAIL_ADDRESS_PLACEHOLDER")}
              onChange={(event) => setSlug(event.target.value)}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupText>@{identity.domain}</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </SettingsField>
      </SettingsWithPreview>

      <AlertDialog
        open={confirming}
        onOpenChange={(open) => !update.isPending && setConfirming(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate("MC_TPL_EMAIL_ADDRESS_CONFIRM_TITLE")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate("MC_TPL_EMAIL_ADDRESS_CONFIRM_DESC")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Typography as="inlineCode" className="text-center">
            {trimmed}@{identity.domain}
          </Typography>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={update.isPending}>
              {translate("MC_TPL_CANCEL")}
            </AlertDialogCancel>
            <PendingButton pending={update.isPending} onClick={onConfirm}>
              {translate("MC_TPL_EMAIL_ADDRESS_CONFIRM_CTA")}
            </PendingButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  )
}

/** The sending address; also opened from the inquiry draft's side sheet. */
export function SendingAddressSection() {
  const { data, isLoading, isError, isFetching, refetch } =
    useEmailSenderIdentity()
  if (data) return <SendingAddressForm identity={data} />
  return (
    <SettingsSection>
      {isLoading && !isError ? (
        <SettingsSkeleton rows={1} />
      ) : (
        <SettingsLoadError
          pending={isFetching}
          onRetry={() => void refetch()}
        />
      )}
    </SettingsSection>
  )
}

function EmailSignatureForm({
  saved,
  withPreview,
}: {
  saved: InquiryTemplateSettings
  withPreview: boolean
}) {
  const { translate, currentLanguage } = useTranslation()
  const saver = useTemplateSettingsSave()
  const [language, setLanguage] = useState<ContentLanguage>(
    currentLanguage === "DE" ? "de" : "en",
  )
  const [signatures, setSignatures] = useSavedDraft(
    saved.emailSignatures,
    sameContent,
  )
  const dirty =
    signatures.en !== saved.emailSignatures.en ||
    signatures.de !== saved.emailSignatures.de
  // As typed: the switch and the notice follow the edit before it is saved.
  const needsWriting = languagesNeedingWriting(signatures)

  const field = (
    <SettingsField
      htmlFor="settings-email-signature"
      label={translate("SETTINGS/EMAIL_SENDING/SIGNATURE_LABEL", {
        language: language.toUpperCase(),
      })}
    >
      <Textarea
        id="settings-email-signature"
        rows={6}
        value={signatures[language]}
        disabled={!saver.canEdit}
        aria-describedby={
          needsWriting[language] ? "settings-email-signature-todo" : undefined
        }
        className={cn(needsWriting[language] && NEEDS_WRITING_FIELD_CLASS)}
        onChange={(event) =>
          setSignatures({ ...signatures, [language]: event.target.value })
        }
      />
      <PlaceholderNotice
        id="settings-email-signature-todo"
        text={signatures[language]}
      />
    </SettingsField>
  )

  return (
    <SettingsSection
      title={translate("SETTINGS/EMAIL_SENDING/SIGNATURE_TITLE")}
      target="email.signature"
      info={translate("SETTINGS/INFO/ALSO_RUNNING")}
      description={translate("MC_TPL_EC_SIGNATURE_NOTE")}
      needsSetup={emailSignatureNeedsSetup(saved)}
      action={
        <LanguageSwitch
          value={language}
          onChange={setLanguage}
          needsWriting={needsWriting}
        />
      }
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="signature"
          dirty={dirty}
          patch={() => ({ emailSignatures: signatures })}
        />
      }
    >
      {withPreview ? (
        <SettingsWithPreview
          preview={
            <EmailPreview
              subject={resolvePreviewVariables(
                saved.emailTemplates.first[language].subject,
                language,
              )}
              footer={signatures[language]}
            />
          }
        >
          {field}
        </SettingsWithPreview>
      ) : (
        field
      )}
    </SettingsSection>
  )
}

/** The signature under every email to suppliers and customers. Also opened
 *  from the inquiry composer, beside the draft, without its preview. */
export function EmailSignatureSection({
  withPreview = true,
}: {
  withPreview?: boolean
}) {
  return (
    <TemplateSettingsLoader>
      {(settings) => (
        <EmailSignatureForm saved={settings} withPreview={withPreview} />
      )}
    </TemplateSettingsLoader>
  )
}

export function EmailSendingPage() {
  const { translate } = useTranslation()
  return (
    <SettingsPage
      title={translate("SETTINGS/EMAIL_SENDING/TITLE")}
      description={translate("SETTINGS/EMAIL_SENDING/DESCRIPTION")}
      notice={translate("SETTINGS/GENERAL/NOTICE")}
      width="wide"
    >
      <SendingAddressSection />
      <EmailSignatureSection />
    </SettingsPage>
  )
}
